import { useState, useEffect } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Tooltip,
  Toolbar,
} from '@mui/material'
import {
  Home,
  Explore,
  Map,
  FolderSpecial,
  Leaderboard,
  EmojiEvents,
  People,
  Flag,
  Build,
  QrCodeScanner,
  CardGiftcard,
  PermMedia,
} from '@mui/icons-material'
import { useAuthStore } from '@store/authStore'
import { hasPendingModerationTickets } from '@api/services/tickets.api'
import { ScanCodeDialog } from '@components/common/ScanCodeDialog'

export const SIDEBAR_WIDTH_FULL = 240
export const SIDEBAR_WIDTH_ICONS = 64
export const SIDEBAR_WIDTH = SIDEBAR_WIDTH_FULL // For backwards compatibility

// Sidebar collapse modes: 0 = hidden, 1 = icons only, 2 = full
export type SidebarCollapseMode = 0 | 1 | 2

interface NavItem {
  label: string
  path: string
  icon: React.ReactNode
  requiresAuth?: boolean
  requiresAdmin?: boolean
  requiresModerator?: boolean
}

interface SidebarProps {
  mobileOpen?: boolean
  onDrawerToggle?: () => void
  isMobile?: boolean
  collapseMode?: SidebarCollapseMode
  onCollapseModeChange?: (mode: SidebarCollapseMode) => void
}

// Guest navigation items (not authenticated)
const guestNavItems: NavItem[] = [
  {
    label: 'Welcome Center',
    path: '/',
    icon: <Home />,
  },
  {
    label: 'About Us',
    path: '/about',
    icon: <Explore />,
  },
  {
    label: 'Features',
    path: '/features',
    icon: <EmojiEvents />,
  },
  {
    label: 'Build Adventures',
    path: '/build',
    icon: <Build />,
  },
]

// Authenticated user navigation items
const authenticatedNavItems: NavItem[] = [
  {
    label: 'Base Camp',
    path: '/',
    icon: <Home />,
  },
  {
    label: 'Adventure Trails',
    path: '/trails',
    icon: <Explore />,
  },
  {
    label: 'Discover Adventures',
    path: '/adventures',
    icon: <Map />,
  },
  {
    label: 'My Adventures',
    path: '/my-adventures',
    icon: <FolderSpecial />,
  },
  // Divider after index 3 (My Adventures)
  {
    label: 'Social',
    path: '/social',
    icon: <People />,
  },
  {
    label: 'Media Manager',
    path: '/media',
    icon: <PermMedia />,
  },
  // Divider after index 5 (Media Manager)
  {
    label: 'Leaderboard',
    path: '/leaderboard',
    icon: <Leaderboard />,
  },
  {
    label: 'Merit Badges',
    path: '/badges',
    icon: <EmojiEvents />,
  },
  {
    label: 'My Rewards',
    path: '/rewards',
    icon: <CardGiftcard />,
  },
  // Divider after index 8 (My Rewards)
  {
    label: 'Ticket Management',
    path: '/tickets',
    icon: <Flag />,
    requiresModerator: true,
  },
]

export function Sidebar({
  mobileOpen = false,
  onDrawerToggle,
  isMobile = false,
  collapseMode = 2,
  onCollapseModeChange,
}: SidebarProps) {
  const location = useLocation()
  const { isAuthenticated, user } = useAuthStore()
  const isAdmin = user?.roles?.includes('Admin')
  const isModerator = user?.roles?.includes('Moderator') || isAdmin
  const [hasPendingTickets, setHasPendingTickets] = useState(false)
  const [scanCodeOpen, setScanCodeOpen] = useState(false)

  const isIconsOnly = collapseMode === 1
  const isHidden = collapseMode === 0
  const currentWidth = isIconsOnly ? SIDEBAR_WIDTH_ICONS : SIDEBAR_WIDTH_FULL

  // Check for pending moderation tickets when user is authenticated
  // This checks both system-level tickets (for Moderators/Admins) and group-level tickets
  useEffect(() => {
    if (isAuthenticated) {
      hasPendingModerationTickets()
        .then((hasPending) => {
          setHasPendingTickets(hasPending)
        })
        .catch((error) => {
          console.error('Error checking pending tickets:', error)
          // On error, default to hiding the menu item
          setHasPendingTickets(false)
        })
    } else {
      setHasPendingTickets(false)
    }
  }, [isAuthenticated])

  // Choose navigation items based on authentication status
  const navItems = isAuthenticated ? authenticatedNavItems : guestNavItems

  // Filter admin and moderator items if needed
  const filteredNavItems = navItems.filter((item) => {
    if (item.requiresAdmin && !isAdmin) return false
    // For ticket management, show based on pending tickets instead of role
    if (item.path === '/tickets') {
      return hasPendingTickets
    }
    // For other moderator items, check role
    if (item.requiresModerator && !isModerator) return false
    return true
  })

  // Scan Code button component
  const scanCodeButton = isAuthenticated && (
    <ListItem disablePadding>
      {isIconsOnly ? (
        <Tooltip title="Scan Code" placement="right" arrow>
          <ListItemButton
            onClick={() => {
              setScanCodeOpen(true)
              if (isMobile) onDrawerToggle?.()
            }}
            sx={{
              borderRadius: 3,
              mb: 0.5,
              justifyContent: 'center',
              px: 1.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'primary.main', minWidth: 'auto' }}>
              <QrCodeScanner />
            </ListItemIcon>
          </ListItemButton>
        </Tooltip>
      ) : (
        <ListItemButton
          onClick={() => {
            setScanCodeOpen(true)
            if (isMobile) onDrawerToggle?.()
          }}
          sx={{
            borderRadius: 3,
            mb: 0.5,
            px: 2,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
            <QrCodeScanner />
          </ListItemIcon>
          <ListItemText
            primary="Scan Code"
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: 400,
            }}
          />
        </ListItemButton>
      )}
    </ListItem>
  )

  const drawerContent = (
    <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      <List sx={{ px: isIconsOnly ? 0.5 : 1, pt: 2, flex: 1 }}>
        {filteredNavItems.map((item, index) => {
          const isActive = location.pathname === item.path || (item.path === '/social' && location.pathname.startsWith('/social'))
          // Add scan code button after My Adventures (index 3)
          const showScanCodeAfter = index === 3
          const isDividerAfter = index === 3 || index === 5 || index === 8

          const buttonContent = (
            <ListItemButton
              component={RouterLink}
              to={item.path}
              selected={isActive}
              onClick={isMobile ? onDrawerToggle : undefined}
              sx={{
                borderRadius: 3,
                mb: 0.5,
                justifyContent: isIconsOnly ? 'center' : 'flex-start',
                px: isIconsOnly ? 1.5 : 2,
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActive ? 'primary.contrastText' : 'primary.main',
                  minWidth: isIconsOnly ? 'auto' : 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!isIconsOnly && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              )}
            </ListItemButton>
          )

          return (
            <Box key={item.path}>
              <ListItem disablePadding>
                {isIconsOnly ? (
                  <Tooltip title={item.label} placement="right" arrow>
                    {buttonContent}
                  </Tooltip>
                ) : (
                  buttonContent
                )}
              </ListItem>
              {showScanCodeAfter && scanCodeButton}
              {isDividerAfter && <Divider sx={{ my: 1 }} />}
            </Box>
          )
        })}
      </List>
    </Box>
  )

  const drawerStyles = {
    width: currentWidth,
    flexShrink: 0,
    transition: 'width 0.2s ease-in-out',
    '& .MuiDrawer-paper': {
      width: currentWidth,
      height: '100vh',
      top: 0,
      boxSizing: 'border-box',
      borderRight: '2px solid',
      borderColor: 'primary.main',
      backgroundImage: `url('/assets/images/Navigation_Background.png')`,
      backgroundSize: '400px',
      backgroundRepeat: 'repeat',
      transition: 'width 0.2s ease-in-out',
      overflowX: 'hidden',
      overflowY: 'auto',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'primary.main',
        opacity: 0.25,
        zIndex: 0,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'background.paper',
        opacity: 0.8,
        zIndex: 0,
      },
    },
  }

  return (
    <Box
      component="nav"
      sx={{
        width: { md: isHidden ? 0 : currentWidth },
        flexShrink: { md: 0 },
        transition: 'width 0.2s ease-in-out',
        position: 'relative',
      }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          ...drawerStyles,
          width: SIDEBAR_WIDTH_FULL, // Always full width on mobile
          '& .MuiDrawer-paper': {
            ...drawerStyles['& .MuiDrawer-paper'],
            width: SIDEBAR_WIDTH_FULL,
          },
        }}
      >
        <Toolbar />
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      {!isHidden && (
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: currentWidth,
            flexShrink: 0,
            transition: 'width 0.2s ease-in-out',
            '& .MuiDrawer-paper': {
              ...drawerStyles['& .MuiDrawer-paper'],
            },
          }}
          open
        >
          <Toolbar />
          {drawerContent}
        </Drawer>
      )}

      {/* Scan Code Dialog - rendered once outside drawers to prevent duplicate instances */}
      <ScanCodeDialog open={scanCodeOpen} onClose={() => setScanCodeOpen(false)} />
    </Box>
  )
}
